/**
 * useTheme — Dark mode management with system preference support.
 *
 * Three modes: 'light' | 'dark' | 'system' (default)
 * Persists to localStorage under 'trendy_theme'.
 * Applies/removes 'dark' class on <html> and updates <meta theme-color>.
 *
 * Usage:
 *   <ThemeProvider><App /></ThemeProvider>
 *   const { theme, isDark, setTheme } = useTheme()
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react'

const ThemeContext = createContext(null)

const THEME_KEY = 'trendy_theme'

function getSystemPreference() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function applyTheme(isDark) {
  if (isDark) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
  // Update mobile browser chrome color
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) {
    meta.setAttribute('content', isDark ? '#0f1117' : '#0072CE')
  }
}

function resolveIsDark(theme) {
  if (theme === 'dark') return true
  if (theme === 'light') return false
  return getSystemPreference() // 'system' or fallback
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    try {
      return localStorage.getItem(THEME_KEY) || 'system'
    } catch {
      return 'system'
    }
  })

  const [isDark, setIsDark] = useState(() => resolveIsDark(theme))

  // Re-resolve isDark whenever theme preference changes
  useEffect(() => {
    setIsDark(resolveIsDark(theme))
  }, [theme])

  // Apply dark class whenever resolved value changes
  useEffect(() => {
    applyTheme(isDark)
  }, [isDark])

  // Listen for OS preference changes when in 'system' mode
  useEffect(() => {
    if (theme !== 'system') return

    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e) => {
      setIsDark(e.matches)
      applyTheme(e.matches)
    }
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [theme])

  const setTheme = useCallback((newTheme) => {
    setThemeState(newTheme)
    try {
      localStorage.setItem(THEME_KEY, newTheme)
    } catch {
      // localStorage might be unavailable
    }
  }, [])

  return (
    <ThemeContext.Provider value={{ theme, isDark, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export default function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
